import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const BranchContext = createContext(null);

export const BranchProvider = ({ children }) => {
  const { api, isAuthenticated, user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user can switch branches (only admin/gerente without assigned branch, OR multiple branches available)
  const canSwitchBranch = user && (
    (user.role === 'admin' || user.role === 'gerente' || user.role === 'administrador') && !user.branch_id
  ) && branches.length > 1;

  // Load branches when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadBranches();
    }
  }, [isAuthenticated]);

  // Set user's assigned branch OR load from localStorage
  useEffect(() => {
    if (branches.length > 0) {
      // If user has assigned branch, use it (unless they're admin)
      if (user?.branch_id && user.role !== 'admin') {
        const userBranch = branches.find(b => b.branch_id === user.branch_id);
        if (userBranch) {
          setCurrentBranch(userBranch);
          localStorage.setItem('currentBranchId', userBranch.branch_id);
          return;
        }
      }

      // For admin/gerente or users without branch: check localStorage
      const savedBranchId = localStorage.getItem('currentBranchId');
      if (savedBranchId) {
        const branch = branches.find(b => b.branch_id === savedBranchId);
        if (branch) {
          setCurrentBranch(branch);
          return;
        }
      }

      // Default to matriz
      const matriz = branches.find(b => b.is_matriz);
      if (matriz) {
        setCurrentBranch(matriz);
        localStorage.setItem('currentBranchId', matriz.branch_id);
      }
    }
  }, [branches, user]);

  const loadBranches = async () => {
    try {
      setLoading(true);
      const response = await api.get('/branches');
      setBranches(response.data);
    } catch (error) {
      console.error('Error loading branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectBranch = (branch) => {
    // Only allow switching if user has permission
    if (!canSwitchBranch) {
      console.warn('User cannot switch branches');
      return;
    }
    setCurrentBranch(branch);
    localStorage.setItem('currentBranchId', branch.branch_id);
  };

  const refreshBranches = () => {
    loadBranches();
  };

  // Check if current branch is matriz
  const isMatriz = currentBranch?.is_matriz || false;

  // Get branch name
  const branchName = currentBranch?.name || 'Sin sucursal';

  return (
    <BranchContext.Provider value={{
      branches,
      currentBranch,
      selectBranch,
      refreshBranches,
      loading,
      isMatriz,
      branchName,
      branchId: currentBranch?.branch_id || null,
      canSwitchBranch  // New: indicates if user can change branches
    }}>
      {children}
    </BranchContext.Provider>
  );
};

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

export default BranchContext;
